import React from "react";

import BASELoading from "../../../../../../../@BASE/core/BASELoading";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import BASESvgIcon from "../../../../../../../@BASE/core/BASESvgIcon";

import EMentor from "../components/EMentor";
import Instructions from "../components/Instructions";
import ComponentCard from "../components/Component";
import ComponentPreview from "../components/Preview";
import ComponentStepLabel from "../components/StepLabel";
import SubComponentCard from "../components/SubComponent";

export default function RegisterAnalysis(props) {
  const { data, dir, ins = [], course, comments = [] } = props;
  const tl = `${dir === "rtl" ? "text-18" : "text-14"} ${course?.language?.acronym}`.trim();

  if (!data) {
    return <BASELoading />;
  }

  // filer comments by component_id
  const componentCommentsCount = (component) => {
    return comments.filter(
      (comment) =>
        comment.component === component && comment.internality !== true
    ).length;
  };

  return (
    <div>
      <style>
        {`
          .wysiwyg p {
            margin: 0px;
          }
        `}
      </style>
      <Typography className="text-16 font-bold p-16 pb-0">
        Page Instructions:
      </Typography>

      <Instructions
        ins={ins}
        course={course}
        dir={dir}
        tl={tl}
        componentCommentsCount={componentCommentsCount}
      />
      <Typography className="text-16 font-bold p-16 pb-0">
        Page Content:
      </Typography>

      <Paper elevation={0} variant="outlined" square className="p-16 m-16">
        {data.materials.map((material, num) => (
          <Grid
            key={`materials-${material.material_title_en}-${num}`}
            container
            spacing={0}
          >
            <Grid item xs={12}>
              <Typography className="text-14 font-bold m-0">
                Authentic {material.media_material ? "Listening" : "Reading"}{" "}
                Material:
              </Typography>
              <Divider className="my-16" />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={0}>
                <Grid item xs={12} sm={4} lg={2}>
                  <Typography className="text-14 font-semibold m-0">
                    Title (TL):
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8} lg={10}>
                  <Typography className={`${tl} m-0`} dir={dir}>
                    {material.material_title_tl}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} lg={2}>
                  <Typography className="text-14 font-semibold m-0">
                    Title (EN):
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8} lg={10}>
                  <Typography className="text-14 m-0">
                    {material.material_title_en}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} lg={2}>
                  <Typography className="text-14 font-semibold m-0">
                    Link:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8} lg={10}>
                  <Typography className="text-14 m-0">
                    {material.link}
                  </Typography>
                </Grid>
                {material.sp_url && (
                  <>
                    <Grid item xs={12} sm={4} lg={2}>
                      <Typography className="text-14 font-semibold m-0">
                        Sharepoint Source:
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} lg={10}>
                      <Button
                        variant="contained"
                        endIcon={
                          <BASESvgIcon size={20}>
                            heroicons-outline:external-link
                          </BASESvgIcon>
                        }
                        color="secondary"
                        size="small"
                        href={material.sp_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          bgcolor: "#013bb1 !important",
                          color: "#ffffff !important",
                          "&:hover": { bgcolor: "#001680 !important" },
                        }}
                      >
                        View Material
                      </Button>
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={4} lg={2}>
                  <Typography className="text-14 font-semibold m-0">
                    Material Type:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8} lg={10}>
                  <Typography className="text-14 m-0">
                    {material.media_material
                      ? `Listening Material`
                      : `Reading Material`}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Paper>

      <Paper elevation={0} variant="outlined" square className="p-16 m-16">
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Typography className="text-14 font-bold m-0">
                  Document Register:
                </Typography>
                <Divider className="my-16" />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <Grid item xs={4} sm={2}>
                    <Typography className="text-14 font-bold m-0">
                      Document is formal:
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={10}>
                    <Typography className="text-14 m-0">
                      {data.is_formal ? "true" : "false"}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} className="mt-16">
                <Grid container spacing={1}>
                  <Grid item xs={4} sm={2}>
                    <Typography className="text-14 font-bold m-0">
                      Summary:
                    </Typography>
                  </Grid>
                  <Grid item xs sm>
                    <Typography className={`${tl} m-0`} dir={dir}>
                      {data.summary}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {data.statement_sets.map((set, index) => (
        <div key={`statement-set-${set.summary}-${index}`}>
          <Typography className="text-14 font-bold p-16 pb-0">
            Statement Set {index + 1}:
          </Typography>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12} className="mt-16">
                <Grid container columns={24} spacing={0}>
                  <Grid item xs={4}>
                    <Typography className="text-14 font-bold m-0">
                      Original Statement:
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography className={`${tl} m-0`} dir={dir}>
                      {set.original}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography className="text-14 font-bold m-0">
                  Statements:
                </Typography>
                <Divider className="my-16" />
                <Grid container spacing={0}>
                  {set.statements.map((statement, index) => (
                    <Grid
                      key={`statement-${statement.statement_text}-${index}`}
                      item
                      xs={12}
                    >
                      {index > 0 && <Divider className="my-8" />}
                      <Grid container spacing={0} columns={24}>
                        <Grid
                          item
                          xs={4}
                          className={`mb-16 ${index > 0 && `mt-6`}`}
                        >
                          <Typography className="text-14 font-semibold m-0">
                            Statement Type:
                          </Typography>
                        </Grid>
                        <Grid item xs className={index > 0 && `mt-6`}>
                          <Typography className="text-14 m-0">
                            {statement.type}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid container spacing={0} columns={24}>
                        <Grid item xs={4}>
                          <Grid item>
                            <Typography className="text-14 font-semibold m-0">
                              Statement:
                            </Typography>
                          </Grid>
                          {statement.is_correct ? (
                            <>
                              <Grid item container direction="row">
                                <Typography className="text-14 font-semibold m-0">
                                  Correct
                                </Typography>
                                <BASESvgIcon
                                  className="text-green-600 mx-2"
                                  size={20}
                                >
                                  heroicons-solid:check-circle
                                </BASESvgIcon>
                              </Grid>
                            </>
                          ) : (
                            <>
                              <Grid item container direction="row">
                                <Typography className="text-14 font-semibold m-0">
                                  Incorrect
                                </Typography>
                                <BASESvgIcon
                                  className="text-red-600 mx-2"
                                  size={20}
                                >
                                  heroicons-solid:x-circle
                                </BASESvgIcon>
                              </Grid>
                            </>
                          )}
                        </Grid>
                        <Grid item xs>
                          <Typography
                            className={`${tl} m-0 ${
                              index < set.statements.length - 1 && `mb-8`
                            }`}
                            dir={dir}
                          >
                            {statement.statement_text}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Typography className="text-14 font-bold m-0">
                      Correct Feedback:
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      className={`${tl} m-0 wysiwyg`}
                      dir={dir}
                      dangerouslySetInnerHTML={{ __html: set.correct_feedback }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Typography className="text-14 font-bold m-0">
                      First Incorrect Feedback:
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      className={`${tl} m-0 wysiwyg`}
                      dir={dir}
                      dangerouslySetInnerHTML={{
                        __html: set.incorrect_feedback_1,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Typography className="text-14 font-bold m-0">
                      Second Incorrect Feedback:
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      className={`${tl} m-0 wysiwyg`}
                      dir={dir}
                      dangerouslySetInnerHTML={{
                        __html: set.incorrect_feedback_2,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Typography className="text-14 font-bold m-0">
                      Final Incorrect Feedback:
                    </Typography>
                    <Divider className="my-16" />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      className={`${tl} m-0 wysiwyg`}
                      dir={dir}
                      sx={{ margin: "0" }}
                      dangerouslySetInnerHTML={{
                        __html: set.incorrect_feedback_3,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} variant="outlined" square className="p-16 m-16">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography className="text-14 font-bold m-0">
                  Statement Options:
                </Typography>
                <Divider className="my-16" />
                <Grid container spacing={1}>
                  <Grid item xs={4} sm={3}>
                    <Typography className="text-14 font-semibold m-0">
                      Maximum Number of Tries:
                    </Typography>
                  </Grid>
                  <Grid item xs={8} sm={9}>
                    <Typography className="text-14 m-0">
                      {set.max_number_of_tries}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </div>
      ))}
      {data.eMentor.length !== 0 && (
        <EMentor
          eMentor={data.eMentor}
          tl={tl}
          dir={dir}
          componentCommentsCount={componentCommentsCount}
        />
      )}
    </div>
  );
}
